import React, { type FC, useEffect, useState } from 'react'
import { Icon, Input, Card, Button } from '@awell-health/design-system'
import type { Organization } from '@stytch/vanilla-js'
import { isEmpty, isNil } from 'lodash'

interface SelectOrganizationInlineProps {
  switchOrganization: (organizationId: string) => Promise<void>
  organizations: Array<Organization>
}

export const SelectOrganizationInline: FC<SelectOrganizationInlineProps> = ({ switchOrganization, organizations }) => {
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [filterString, setFilterString] = useState('')
  const [filteredOrganizations, setFilteredOrganizations] = useState(organizations)
  const [isSwitching, setIsSwitching] = useState(false)

  useEffect(() => {
    if (isEmpty(filterString)) {
      setFilteredOrganizations(organizations)
    } else {
      setFilteredOrganizations(
        organizations.filter(org => {
          return (
            org.organization_name.toLowerCase().includes(filterString.toLowerCase()) ||
            org.organization_slug.toLowerCase().includes(filterString.toLowerCase()) ||
            org.organization_id.toLowerCase().includes(filterString.toLowerCase())
          )
        })
      )
    }
  }, [filterString, organizations])

  const handleSwitch = async (organizationId: string) => {
    setIsSwitching(true)
    try {
      await switchOrganization(organizationId)
    } catch (error) {
      console.error('Failed to switch organization:', error)
      setIsSwitching(false)
    }
  }

  return (
    <div className="w-full">
      <Input
        label="Search organization"
        autoFocus
        value={filterString}
        onChange={e => {
          setFilterString(e.target.value)
        }}
        onKeyDown={async e => {
          if (e.key === 'Enter' && filteredOrganizations.length === 1 && isNil(selectedOrganization)) {
            await handleSwitch(filteredOrganizations[0].organization_id)
          }
        }}
        placeholder="Type to search by name, slug, or ID..."
      />

      {!isNil(selectedOrganization) && (
        <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded">
          <span>Selected: {selectedOrganization.organization_name}</span>
          <button
            onClick={() => {
              setSelectedOrganization(null)
            }}
            className="text-red-500 hover:text-red-700"
          >
            <Icon icon="RiDeleteBinLine" />
          </button>
        </div>
      )}

      <div className="mt-4 max-h-96 overflow-y-auto">
        {filteredOrganizations.length === 0 ? (
          <div className="text-center text-gray-500 py-4">No organizations found matching your search.</div>
        ) : (
          <div className="space-y-2">
            {filteredOrganizations.map(org => (
              <Card className="w-full p-4" key={org.organization_id}>
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">{org.organization_name}</h4>
                    <p className="text-sm text-gray-500">{org.organization_slug}</p>
                  </div>
                  <Button
                    onClick={() => handleSwitch(org.organization_id)}
                    disabled={isSwitching}
                    variant="secondary"
                    size="small"
                  >
                    Switch
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
